import { useState } from 'react';
import { 
  X, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  File,
  Search,
  Tag,
  Calendar,
  HardDrive,
  Eye,
  CheckCircle2
} from 'lucide-react';
import { 
  allResources, 
  designTemplates, 
  codeReferences, 
  calculationGuides,
  searchResources,
  generateResourceFile,
  type Resource 
} from '../data/resources';

interface ResourcesModalProps {
  onClose: () => void;
  initialCategory?: 'template' | 'code_reference' | 'calculator' | 'guide' | 'all';
}

export function ResourcesModal({ onClose, initialCategory = 'all' }: ResourcesModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set());

  const getFilteredResources = (): Resource[] => {
    let resources: Resource[] = [];
    
    if (searchQuery) {
      resources = searchResources(searchQuery);
    } else {
      switch (selectedCategory) {
        case 'template':
          resources = designTemplates;
          break;
        case 'code_reference':
          resources = codeReferences;
          break;
        case 'guide':
          resources = calculationGuides;
          break;
        default:
          resources = allResources;
      }
    }
    
    return resources;
  };

  const handleDownload = async (resource: Resource) => {
    setDownloadingId(resource.id);
    
    try {
      // Simulate download delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate file content
      const blob = generateResourceFile(resource);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resource.id}.${resource.fileType}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Mark as downloaded
      setDownloadedIds(prev => new Set([...prev, resource.id]));
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const getFileIcon = (fileType: Resource['fileType']) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="w-5 h-5" />;
      case 'xlsx':
        return <FileSpreadsheet className="w-5 h-5" />;
      case 'docx':
        return <File className="w-5 h-5" />;
      case 'zip':
        return <HardDrive className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const getFileColor = (fileType: Resource['fileType']) => {
    switch (fileType) {
      case 'pdf':
        return 'text-red-600 bg-red-50';
      case 'xlsx':
        return 'text-green-600 bg-green-50';
      case 'docx':
        return 'text-blue-600 bg-blue-50';
      case 'zip':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  const filteredResources = getFilteredResources();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold mb-2">Additional Resources</h2>
              <p className="text-blue-100">Download templates, code references, and calculation guides</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                All ({allResources.length})
              </button>
              <button
                onClick={() => { setSelectedCategory('template'); setSearchQuery(''); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  selectedCategory === 'template' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Templates ({designTemplates.length})
              </button>
              <button
                onClick={() => { setSelectedCategory('code_reference'); setSearchQuery(''); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  selectedCategory === 'code_reference' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Code Refs ({codeReferences.length})
              </button>
              <button
                onClick={() => { setSelectedCategory('guide'); setSearchQuery(''); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  selectedCategory === 'guide' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Guides ({calculationGuides.length})
              </button>
            </div>
          </div>
        </div>

        {/* Resources List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No resources found</h3>
              <p className="text-slate-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className="bg-white border-2 border-slate-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-3 rounded-lg ${getFileColor(resource.fileType)}`}>
                        {getFileIcon(resource.fileType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 mb-1 line-clamp-1">{resource.title}</h3>
                        <p className="text-sm text-slate-600 mb-2 line-clamp-2">{resource.description}</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {resource.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center">
                        <HardDrive className="w-3 h-3 mr-1" />
                        {resource.fileSize}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(resource.lastUpdated).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      {resource.previewAvailable && (
                        <button
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(resource)}
                        disabled={downloadingId === resource.id}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                          downloadedIds.has(resource.id)
                            ? 'bg-green-50 text-green-700 border-2 border-green-200'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {downloadingId === resource.id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm">Downloading...</span>
                          </>
                        ) : downloadedIds.has(resource.id) ? (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm">Downloaded</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            <span className="text-sm">Download</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 p-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {filteredResources.length} of {allResources.length} resources
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
